import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';
import { TrendingDown, TrendingUp, DollarSign, BarChart3, AlertCircle, Target } from 'lucide-react';
import PerformanceChart from './PerformanceChart';

const Performance = () => {
  const [summary, setSummary] = useState(null);
  const [topPerformers, setTopPerformers] = useState([]);
  const [worstPerformers, setWorstPerformers] = useState([]);
  const [byActivist, setByActivist] = useState([]);
  const [ultraShortFund, setUltraShortFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const [summaryRes, topRes, worstRes, activistRes, ultraRes] = await Promise.all([
        apiClient.get('/api/performance/summary'),
        apiClient.get('/api/performance/top-performers?limit=10'),
        apiClient.get('/api/performance/worst-performers?limit=10'),
        apiClient.get('/api/performance/by-activist'),
        apiClient.get('/api/performance/ultra-short-fund')
      ]);
      
      setSummary(summaryRes.data);
      setTopPerformers(topRes.data);
      setWorstPerformers(worstRes.data);
      setByActivist(activistRes.data);
      setUltraShortFund(ultraRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching performance data:', err);
      if (err.response && err.response.status === 503) {
        setError('Performance tracking is not yet configured on this server. Please run the setup script to enable this feature.');
      } else {
        setError('Failed to load performance data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Format return from SHORT SELLER perspective (inverted from stock price change)
  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const num = -parseFloat(value); // Invert: stock down = short seller profit (positive)
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  // Color coding: GREEN = profit (stock fell), RED = loss (stock rose)
  const getReturnColor = (value) => {
    if (value === null || value === undefined) return 'text-gray-600';
    const num = -parseFloat(value); // Invert: stock down = short seller profit
    if (num > 20) return 'text-green-600 font-bold';
    if (num > 10) return 'text-green-500';
    if (num > 0) return 'text-green-400';
    if (num === 0) return 'text-gray-600';
    if (num > -10) return 'text-red-400';
    if (num > -20) return 'text-red-500';
    return 'text-red-600 font-bold';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading performance data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Short Seller Performance</h1>
        <p className="text-gray-600 mt-2">
          Track short seller returns after reports are published (positive % = profit from stock decline)
        </p>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center space-x-3">
              <Target className="w-8 h-8 text-blue-600" />
              <div>
                <h4 className="text-sm font-medium text-gray-600">Reports Tracked</h4>
                <p className="text-2xl font-bold text-gray-900">{summary.total_with_data || 0}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center space-x-3">
              <BarChart3 className="w-8 h-8 text-purple-600" />
              <div>
                <h4 className="text-sm font-medium text-gray-600">Avg Short Return</h4>
                <p className={`text-2xl font-bold ${getReturnColor(summary.avg_return)}`}>
                  {formatPercent(summary.avg_return)}
                </p>
                <p className="text-xs text-gray-500">1M P&L (stock fell = profit)</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center space-x-3">
              <TrendingDown className="w-8 h-8 text-green-600" />
              <div>
                <h4 className="text-sm font-medium text-gray-600">Profitable Shorts</h4>
                <p className="text-2xl font-bold text-green-600">{summary.negative_returns || 0}</p>
                <p className="text-xs text-gray-500">
                  ({summary.total_with_data > 0 ? ((summary.negative_returns / summary.total_with_data) * 100).toFixed(0) : 0}% success)
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center space-x-3">
              <TrendingUp className="w-8 h-8 text-red-600" />
              <div>
                <h4 className="text-sm font-medium text-gray-600">Failed Shorts</h4>
                <p className="text-2xl font-bold text-red-600">{summary.positive_returns || 0}</p>
                <p className="text-xs text-gray-500">
                  ({summary.total_with_data > 0 ? ((summary.positive_returns / summary.total_with_data) * 100).toFixed(0) : 0}% losses)
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time-based Returns */}
      {summary && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Average Returns Over Time</h2>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">1 Day</div>
              <div className={`text-lg font-bold ${getReturnColor(summary.avg_1d_return)}`}>
                {formatPercent(summary.avg_1d_return)}
              </div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">1 Week</div>
              <div className={`text-lg font-bold ${getReturnColor(summary.avg_1w_return)}`}>
                {formatPercent(summary.avg_1w_return)}
              </div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">1 Month</div>
              <div className={`text-lg font-bold ${getReturnColor(summary.avg_1m_return)}`}>
                {formatPercent(summary.avg_1m_return)}
              </div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">3 Months</div>
              <div className={`text-lg font-bold ${getReturnColor(summary.avg_3m_return)}`}>
                {formatPercent(summary.avg_3m_return)}
              </div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">6 Months</div>
              <div className={`text-lg font-bold ${getReturnColor(summary.avg_6m_return)}`}>
                {formatPercent(summary.avg_6m_return)}
              </div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">1 Year</div>
              <div className={`text-lg font-bold ${getReturnColor(summary.avg_1y_return)}`}>
                {formatPercent(summary.avg_1y_return)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Reports Visualization */}
      <PerformanceChart />

      {/* Ultra Short Fund */}
      {ultraShortFund && ultraShortFund.fundPerformance && (
        <div className="card bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
              <Target className="w-6 h-6 mr-2 text-purple-600" />
              Ultra Short Fund
            </h2>
            <p className="text-gray-700">
              Elite short seller performance tracking: {ultraShortFund.activists?.join(', ')}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {ultraShortFund.fundPerformance.total_positions} positions • Weighted average & median returns
            </p>
          </div>

          {/* Time-based Returns Table */}
          <div className="bg-white rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Fund Performance by Time Period</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Period</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Positions</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Weighted Avg</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 bg-purple-50">Median Return</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700 bg-yellow-50">Std Dev</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Success Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">1 Day</td>
                    <td className="py-3 px-4 text-center text-gray-600">{ultraShortFund.fundPerformance.n_1d}</td>
                    <td className={`py-3 px-4 text-right font-semibold ${getReturnColor(ultraShortFund.fundPerformance.avg_1d)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.avg_1d)}
                    </td>
                    <td className={`py-3 px-4 text-right text-lg font-bold bg-purple-50 ${getReturnColor(ultraShortFund.fundPerformance.median_1d)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.median_1d)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-gray-600 bg-yellow-50">
                      {formatPercent(ultraShortFund.fundPerformance.stddev_1d)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        (ultraShortFund.fundPerformance.winners_1d / ultraShortFund.fundPerformance.n_1d) > 0.5 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {((ultraShortFund.fundPerformance.winners_1d / ultraShortFund.fundPerformance.n_1d) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">1 Week</td>
                    <td className="py-3 px-4 text-center text-gray-600">{ultraShortFund.fundPerformance.n_1w}</td>
                    <td className={`py-3 px-4 text-right font-semibold ${getReturnColor(ultraShortFund.fundPerformance.avg_1w)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.avg_1w)}
                    </td>
                    <td className={`py-3 px-4 text-right text-lg font-bold bg-purple-50 ${getReturnColor(ultraShortFund.fundPerformance.median_1w)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.median_1w)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-gray-600 bg-yellow-50">
                      {formatPercent(ultraShortFund.fundPerformance.stddev_1w)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        (ultraShortFund.fundPerformance.winners_1w / ultraShortFund.fundPerformance.n_1w) > 0.5 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {((ultraShortFund.fundPerformance.winners_1w / ultraShortFund.fundPerformance.n_1w) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">1 Month</td>
                    <td className="py-3 px-4 text-center text-gray-600">{ultraShortFund.fundPerformance.n_1m}</td>
                    <td className={`py-3 px-4 text-right font-semibold ${getReturnColor(ultraShortFund.fundPerformance.avg_1m)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.avg_1m)}
                    </td>
                    <td className={`py-3 px-4 text-right text-lg font-bold bg-purple-50 ${getReturnColor(ultraShortFund.fundPerformance.median_1m)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.median_1m)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-gray-600 bg-yellow-50">
                      {formatPercent(ultraShortFund.fundPerformance.stddev_1m)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        (ultraShortFund.fundPerformance.winners_1m / ultraShortFund.fundPerformance.n_1m) > 0.5 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {((ultraShortFund.fundPerformance.winners_1m / ultraShortFund.fundPerformance.n_1m) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">3 Months</td>
                    <td className="py-3 px-4 text-center text-gray-600">{ultraShortFund.fundPerformance.n_3m}</td>
                    <td className={`py-3 px-4 text-right font-semibold ${getReturnColor(ultraShortFund.fundPerformance.avg_3m)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.avg_3m)}
                    </td>
                    <td className={`py-3 px-4 text-right text-lg font-bold bg-purple-50 ${getReturnColor(ultraShortFund.fundPerformance.median_3m)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.median_3m)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-gray-600 bg-yellow-50">
                      {formatPercent(ultraShortFund.fundPerformance.stddev_3m)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        (ultraShortFund.fundPerformance.winners_3m / ultraShortFund.fundPerformance.n_3m) > 0.5 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {((ultraShortFund.fundPerformance.winners_3m / ultraShortFund.fundPerformance.n_3m) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">6 Months</td>
                    <td className="py-3 px-4 text-center text-gray-600">{ultraShortFund.fundPerformance.n_6m}</td>
                    <td className={`py-3 px-4 text-right font-semibold ${getReturnColor(ultraShortFund.fundPerformance.avg_6m)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.avg_6m)}
                    </td>
                    <td className={`py-3 px-4 text-right text-lg font-bold bg-purple-50 ${getReturnColor(ultraShortFund.fundPerformance.median_6m)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.median_6m)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-gray-600 bg-yellow-50">
                      {formatPercent(ultraShortFund.fundPerformance.stddev_6m)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        (ultraShortFund.fundPerformance.winners_6m / ultraShortFund.fundPerformance.n_6m) > 0.5 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {((ultraShortFund.fundPerformance.winners_6m / ultraShortFund.fundPerformance.n_6m) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  <tr className="border-b-2 border-gray-200 hover:bg-gray-50 bg-purple-25">
                    <td className="py-3 px-4 font-bold">1 Year</td>
                    <td className="py-3 px-4 text-center text-gray-600 font-semibold">{ultraShortFund.fundPerformance.n_1y}</td>
                    <td className={`py-3 px-4 text-right font-bold ${getReturnColor(ultraShortFund.fundPerformance.avg_1y)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.avg_1y)}
                    </td>
                    <td className={`py-3 px-4 text-right text-lg font-bold bg-purple-100 ${getReturnColor(ultraShortFund.fundPerformance.median_1y)}`}>
                      {formatPercent(ultraShortFund.fundPerformance.median_1y)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-semibold text-gray-700 bg-yellow-100">
                      {formatPercent(ultraShortFund.fundPerformance.stddev_1y)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-3 py-1 rounded text-sm font-bold ${
                        (ultraShortFund.fundPerformance.winners_1y / ultraShortFund.fundPerformance.n_1y) > 0.5 ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'
                      }`}>
                        {((ultraShortFund.fundPerformance.winners_1y / ultraShortFund.fundPerformance.n_1y) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Performance by Activist */}
          {ultraShortFund.byActivist && ultraShortFund.byActivist.length > 0 && (
            <div className="bg-white rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Activist Rankings (1-Month)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {ultraShortFund.byActivist.map((activist, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 text-sm">{activist.short_seller}</h4>
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">#{index + 1}</span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <div className="text-xs text-gray-500">Median Return</div>
                        <div className={`text-xl font-bold ${getReturnColor(activist.median_1m)}`}>
                          {formatPercent(activist.median_1m)}
                        </div>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Success Rate:</span>
                        <span className={`font-semibold ${
                          (activist.winners_1m / activist.total_1m) > 0.5 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {((activist.winners_1m / activist.total_1m) * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">Reports:</span>
                        <span className="font-semibold">{activist.total_reports}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Positions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Winning Positions */}
            {ultraShortFund.topWinners && ultraShortFund.topWinners.length > 0 && (
              <div className="bg-white rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <TrendingDown className="w-5 h-5 mr-2 text-green-600" />
                  Top 5 Winning Positions
                </h3>
                <div className="space-y-2">
                  {ultraShortFund.topWinners.slice(0, 5).map((position, index) => (
                    <div key={index} className="border-l-4 border-green-500 pl-3 py-2 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">{position.ticker}</div>
                          <div className="text-xs text-gray-600">{position.short_seller}</div>
                        </div>
                        <div className={`text-right font-bold ${getReturnColor(position.return_1m_pct)}`}>
                          {formatPercent(position.return_1m_pct)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Losing Positions */}
            {ultraShortFund.topLosers && ultraShortFund.topLosers.length > 0 && (
              <div className="bg-white rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-red-600" />
                  Top 5 Losing Positions
                </h3>
                <div className="space-y-2">
                  {ultraShortFund.topLosers.slice(0, 5).map((position, index) => (
                    <div key={index} className="border-l-4 border-red-500 pl-3 py-2 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">{position.ticker}</div>
                          <div className="text-xs text-gray-600">{position.short_seller}</div>
                        </div>
                        <div className={`text-right font-bold ${getReturnColor(position.return_1m_pct)}`}>
                          {formatPercent(position.return_1m_pct)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top and Worst Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers (Least Negative) */}
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
            Best Performers
          </h2>
          <div className="space-y-3">
            {topPerformers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No performance data available yet</p>
            ) : (
              topPerformers.map((stock, index) => (
                <div key={stock.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-mono text-gray-500">#{index + 1}</span>
                      <span className="font-semibold text-gray-900">{stock.ticker}</span>
                      <span className="text-sm text-gray-600">{stock.target_company}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      by {stock.short_seller} • {new Date(stock.publication_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${getReturnColor(stock.total_return_pct)}`}>
                      {formatPercent(stock.total_return_pct)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatPrice(stock.report_price)} → {formatPrice(stock.current_price)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Worst Performers */}
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingDown className="w-5 h-5 mr-2 text-red-600" />
            Worst Performers
          </h2>
          <div className="space-y-3">
            {worstPerformers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No performance data available yet</p>
            ) : (
              worstPerformers.map((stock, index) => (
                <div key={stock.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-mono text-gray-500">#{index + 1}</span>
                      <span className="font-semibold text-gray-900">{stock.ticker}</span>
                      <span className="text-sm text-gray-600">{stock.target_company}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      by {stock.short_seller} • {new Date(stock.publication_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${getReturnColor(stock.total_return_pct)}`}>
                      {formatPercent(stock.total_return_pct)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatPrice(stock.report_price)} → {formatPrice(stock.current_price)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Performance by Activist */}
      {byActivist && byActivist.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance by Activist</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Activist</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Reports</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Avg Return</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Best</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Worst</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Success Rate</th>
                </tr>
              </thead>
              <tbody>
                {byActivist.map((activist, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium text-gray-900">{activist.short_seller}</td>
                    <td className="py-3 px-4 text-center text-gray-600">{activist.total_reports}</td>
                    <td className={`py-3 px-4 text-right font-bold ${getReturnColor(activist.avg_return)}`}>
                      {formatPercent(activist.avg_return)}
                    </td>
                    <td className={`py-3 px-4 text-right ${getReturnColor(activist.best_return)}`}>
                      {formatPercent(activist.best_return)}
                    </td>
                    <td className={`py-3 px-4 text-right ${getReturnColor(activist.worst_return)}`}>
                      {formatPercent(activist.worst_return)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        parseFloat(activist.negative_pct) > 75 ? 'bg-green-100 text-green-800' :
                        parseFloat(activist.negative_pct) > 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {parseFloat(activist.negative_pct).toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>Success Rate</strong> = Percentage of reports where the stock declined (negative return). 
              Higher percentage means more accurate short calls.
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="card bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <div className="flex items-start space-x-3">
          <DollarSign className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">About This Data</h3>
            <p className="text-sm text-gray-700 mb-2">
              Stock performance is tracked from the report publication date using real-time data from Alpaca Markets API. 
              Returns are calculated at multiple time intervals (1 day, 1 week, 1 month, 3 months, 6 months, and 1 year).
            </p>
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> Only reports with valid ticker symbols and publication dates can be tracked. 
              Data is updated automatically every 24 hours.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Performance;

